import { useErrorStore } from '../state/store'
import { handleAndLogError, ApiError, ApiErrorCode } from './api-errors'

/**
 * Get contextual error message with resolution steps based on operation
 */
function getContextualErrorMessage(
    error: ApiError,
    context?: Record<string, unknown>
): string {
    const operation = context?.operation as string

    if (error.code === ApiErrorCode.EXCEL_BUSY) {
        if (
            operation?.includes('smart-detection') ||
            operation?.includes('formula') ||
            operation?.includes('batch')
        ) {
            return `
                <strong>Excel is Busy</strong><br/>
                Excel is currently processing another operation and can't complete this request right now.
            `.trim()
        }
        return 'Excel is processing another operation. Please wait a moment and try again.'
    }

    if (error.context?.errorType === 'FormulaParsingError') {
        return `
            <strong>Formula Processing Issue</strong><br/>
            There was an issue processing a formula operation. Your data may not have updated correctly.
        `.trim()
    }

    if (
        operation?.includes('smart-detection') ||
        operation?.includes('formula')
    ) {
        return `
            <strong>Smart Detection Error</strong><br/>
            ${error.userMessage}
        `.trim()
    }

    if (operation?.includes('refresh') || operation?.includes('sync')) {
        return `
            <strong>Refresh Failed</strong><br/>
            ${error.userMessage}
        `.trim()
    }

    if (
        error.code === ApiErrorCode.NETWORK_ERROR ||
        error.code === ApiErrorCode.TIMEOUT
    ) {
        return `
            <strong>Connection Issue</strong><br/>
            ${error.userMessage}
        `.trim()
    }

    return error.userMessage
}

/**
 * Handle an error and optionally show a user-facing notification
 * @param error - The error to handle
 * @param context - Additional context for logging
 * @param options - Options for notification behavior
 */
export function handleErrorWithNotification(
    error: unknown,
    context?: Record<string, unknown>,
    options?: {
        showNotification?: boolean // Default: true
        customMessage?: string // Override with custom message
        skipExcelBusyNotification?: boolean // If true, don't show notifications for Excel busy errors (default: false)
    }
): ApiError {
    const parsedError = handleAndLogError(error, context)

    const shouldShow =
        options?.showNotification !== false &&
        !(
            options?.skipExcelBusyNotification &&
            parsedError.code === ApiErrorCode.EXCEL_BUSY
        )

    if (shouldShow) {
        const { setError } = useErrorStore.getState()

        const message =
            options?.customMessage ||
            getContextualErrorMessage(parsedError, context)

        setError({
            hasError: true,
            errorMessage: message,
        })
    }

    return parsedError
}

/**
 * Clear any displayed error notifications
 */
export function clearErrorNotification() {
    const { setError } = useErrorStore.getState()
    setError({
        hasError: false,
        errorMessage: '',
    })
}
